/**
 * Shared icon registry for hamburger menu items.
 * Used by both admin icon picker and ExpandableFAB web fallback
 * to ensure consistent rendering across native/web.
 */

export interface HamburgerIconDefinition {
  value: string;
  label: string;
  iconName: string;
  webFallback: string;
}

export const HAMBURGER_ICONS: HamburgerIconDefinition[] = [
  // Category icons (reused from existing)
  { value: 'home', label: '🏠 Home', iconName: 'home-outline', webFallback: '🏠' },
  { value: 'room', label: '🏠 Room', iconName: 'bed-outline', webFallback: '🛏️' },
  { value: 'water', label: '💧 Water', iconName: 'water-outline', webFallback: '💧' },
  { value: 'electrical', label: '⚡ Electrical', iconName: 'flash-outline', webFallback: '⚡' },
  { value: 'heating', label: '🔥 Heating', iconName: 'fire', webFallback: '🔥' },
  { value: 'cooling', label: '❄️ Cooling', iconName: 'snowflake', webFallback: '❄️' },
  { value: 'safety', label: '🛡️ Safety', iconName: 'shield-check-outline', webFallback: '🛡️' },
  { value: 'tools', label: '🔧 Tools', iconName: 'wrench-outline', webFallback: '🔧' },
  { value: 'folder', label: '📁 Folder', iconName: 'folder-outline', webFallback: '📁' },
  { value: 'check', label: '✅ Check', iconName: 'check-circle-outline', webFallback: '✅' },

  // Additional hamburger-specific icons
  { value: 'wrench', label: '🔧 Wrench', iconName: 'wrench', webFallback: '🔧' },
  { value: 'hammer', label: '🔨 Hammer', iconName: 'hammer', webFallback: '🔨' },
  { value: 'leaf', label: '🍃 Leaf', iconName: 'leaf', webFallback: '🍃' },
  { value: 'broom', label: '🧹 Broom', iconName: 'broom', webFallback: '🧹' },
  { value: 'clipboard-list', label: '📋 Clipboard', iconName: 'clipboard-list', webFallback: '📋' },
  { value: 'hard-hat', label: '⛑️ Hard Hat', iconName: 'hard-hat', webFallback: '⛑️' },
  { value: 'truck', label: '🚛 Truck', iconName: 'truck', webFallback: '🚛' },
  { value: 'paint-roller', label: '🎨 Paint', iconName: 'paint-roller', webFallback: '🎨' },
  { value: 'clipboard-text', label: '📝 Notes', iconName: 'clipboard-text', webFallback: '📝' },
  { value: 'calendar', label: '📅 Calendar', iconName: 'calendar', webFallback: '📅' },
  { value: 'cog', label: '⚙️ Settings', iconName: 'cog', webFallback: '⚙️' },
  { value: 'account-wrench', label: '👷 Service', iconName: 'account-wrench', webFallback: '👷' },
];

/**
 * Get icon definition by value
 */
export function getHamburgerIcon(value: string): HamburgerIconDefinition | undefined {
  return HAMBURGER_ICONS.find(icon => icon.value === value);
}

/**
 * Get Material Community Icons name for a hamburger icon value
 */
export function getIconName(value: string): string {
  return getHamburgerIcon(value)?.iconName || 'help-circle-outline';
}

/**
 * Get web fallback emoji for a hamburger icon value
 */
export function getWebFallback(value: string): string {
  return getHamburgerIcon(value)?.webFallback || '•';
}
